import { useId, useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { formatPhone, normalizePhoneDigits } from "@/lib/validators";
import { fetchAddress, formatCEP } from "@/lib/cep";
import { useIBGEStates } from "@/hooks/useIBGELocations";
import { User, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { DocumentInput } from "@/components/DocumentInput";
import { validateDocument } from "@/utils/document";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface BuyerData {
  fullName: string;
  email: string;
  birthDate: string;
  cpf: string;
  companyName: string;
  phone: string;
  cep: string;
  street: string;
  addressNumber: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface CheckoutStepBuyerProps {
  buyerData: BuyerData;
  setBuyerData: React.Dispatch<React.SetStateAction<BuyerData>>;
  onNext: () => void;
}

export function CheckoutStepBuyer({ buyerData, setBuyerData, onNext }: CheckoutStepBuyerProps) {
  const { t } = useTranslation("checkout");
  const fieldId = useId();
  const { user, profile } = useAuth();
  const { states } = useIBGEStates();
  const [loadingCep, setLoadingCep] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-fill from profile on first load
  useEffect(() => {
    if (profileLoaded || !user) return;

    if (profile) {
      setBuyerData((prev) => ({
        ...prev,
        fullName: prev.fullName || profile.full_name || "",
        email: prev.email || user.email || "",
        cpf: prev.cpf || profile.document_number || "",
        phone: prev.phone || (profile.phone ? formatPhone(profile.phone) : ""),
        birthDate: prev.birthDate || profile.birth_date || "",
        cep: prev.cep || (profile.cep ? formatCEP(profile.cep) : ""),
        street: prev.street || profile.street || "",
        addressNumber: prev.addressNumber || profile.address_number || "",
        complement: prev.complement || profile.complement || "",
        neighborhood: prev.neighborhood || profile.neighborhood || "",
        city: prev.city || profile.city || "",
        state: prev.state || profile.state || "",
      }));
    } else {
      setBuyerData((prev) => ({
        ...prev,
        email: prev.email || user.email || "",
      }));
    }
    setProfileLoaded(true);
  }, [user, profile, profileLoaded, setBuyerData]);

  const handleFillFromProfile = useCallback(() => {
    if (!profile && !user) return;
    setBuyerData((prev) => ({
      ...prev,
      fullName: profile?.full_name || prev.fullName,
      email: user?.email || prev.email,
      cpf: profile?.document_number || prev.cpf,
      phone: profile?.phone ? formatPhone(profile.phone) : prev.phone,
      birthDate: profile?.birth_date || prev.birthDate,
      cep: profile?.cep ? formatCEP(profile.cep) : prev.cep,
      street: profile?.street || prev.street,
      addressNumber: profile?.address_number || prev.addressNumber,
      complement: profile?.complement || prev.complement,
      neighborhood: profile?.neighborhood || prev.neighborhood,
      city: profile?.city || prev.city,
      state: profile?.state || prev.state,
    }));
  }, [profile, user, setBuyerData]);

  // CEP auto-fill
  const handleCepChange = useCallback(async (rawValue: string) => {
    const formatted = formatCEP(rawValue);
    setBuyerData((prev) => ({ ...prev, cep: formatted }));

    const clean = rawValue.replace(/\D/g, "");
    if (clean.length === 8) {
      setLoadingCep(true);
      const address = await fetchAddress(clean);
      if (address) {
        setBuyerData((prev) => ({
          ...prev,
          street: address.street || prev.street,
          neighborhood: address.neighborhood || prev.neighborhood,
          city: address.city || prev.city,
          state: address.state || prev.state,
        }));
      }
      setLoadingCep(false);
    }
  }, [setBuyerData]);

  const handleValidateAndNext = () => {
    const newErrors: Record<string, string> = {};

    if (!buyerData.fullName.trim()) newErrors.fullName = t("checkout.validation.fillFullName");
    if (!buyerData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerData.email)) newErrors.email = t("checkout.validation.invalidEmail");
    if (!buyerData.birthDate) newErrors.birthDate = t("checkout.validation.fillBirthDate");

    const docValidation = validateDocument(buyerData.cpf);
    if (!docValidation.valid || !docValidation.type) {
      newErrors.cpf = docValidation.error || t("checkout.validation.invalidDocument");
    } else if (docValidation.type === "cnpj" && !buyerData.companyName.trim()) {
      newErrors.companyName = t("checkout.validation.fillCompanyName");
    }

    if (!buyerData.phone.trim() || normalizePhoneDigits(buyerData.phone).length < 10) newErrors.phone = t("checkout.validation.invalidPhone");
    if (!buyerData.cep.trim() || buyerData.cep.replace(/\D/g, "").length !== 8) newErrors.cep = t("checkout.validation.invalidCep");
    if (!buyerData.street.trim()) newErrors.street = t("checkout.validation.fillStreet");
    if (!buyerData.addressNumber.trim()) newErrors.addressNumber = t("checkout.validation.fillNumber");
    if (!buyerData.neighborhood.trim()) newErrors.neighborhood = t("checkout.validation.fillNeighborhood");
    if (!buyerData.city.trim()) newErrors.city = t("checkout.validation.fillCity");
    if (!buyerData.state.trim()) newErrors.state = t("checkout.validation.fillState");

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstInvalid = Object.keys(newErrors)[0];
      requestAnimationFrame(() => {
        const field = document.getElementById(`${fieldId}-${firstInvalid}`);
        field?.focus({ preventScroll: true });
        field?.scrollIntoView({ block: "center", behavior: "instant" });
      });
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
        <div>
          <h2 className="font-display text-[22px] font-bold tracking-tight text-foreground">{t("checkout.buyerSection.title")}</h2>
          <p className="text-[13px] text-muted-foreground mt-1">{t("checkout.buyerSection.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={handleFillFromProfile}
          className="inline-flex min-h-11 items-center gap-1.5 px-3 py-2 rounded-[10px] border border-border bg-transparent text-xs font-semibold text-foreground cursor-pointer whitespace-nowrap shrink-0 hover:bg-muted transition-colors"
        >
          <User className="h-3 w-3" />
          {t("checkout.buyerSection.fillFromProfile")}
        </button>
      </div>

      {/* Form card */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-4 sm:p-6">
        {/* Nome completo */}
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-fullName`} className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.fullName")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input id={`${fieldId}-fullName`} aria-invalid={!!errors.fullName} aria-describedby={errors.fullName ? `${fieldId}-fullName-error` : undefined}
            value={buyerData.fullName}
            onChange={(e) => { setBuyerData((p) => ({ ...p, fullName: e.target.value })); setErrors((p) => ({ ...p, fullName: "" })); }}
            placeholder={t("checkout.buyerSection.fullNamePlaceholder")}
            autoComplete="name"
            className={errors.fullName ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {errors.fullName && <span id={`${fieldId}-fullName-error`} className="text-[11px] text-destructive">{errors.fullName}</span>}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-email`} className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.email")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input id={`${fieldId}-email`} aria-invalid={!!errors.email} aria-describedby={errors.email ? `${fieldId}-email-error` : undefined}
            type="email"
            value={buyerData.email}
            onChange={(e) => { setBuyerData((p) => ({ ...p, email: e.target.value })); setErrors((p) => ({ ...p, email: "" })); }}
            placeholder={t("checkout.buyerSection.emailPlaceholder")}
            autoComplete="email"
            disabled
            className="bg-muted"
          />
          <span className="text-[11px] text-muted-foreground">{t("checkout.buyerSection.emailNotice")}</span>
        </div>

        {/* Data nascimento */}
        <div className="flex flex-col gap-1 max-w-xs">
          <Label htmlFor={`${fieldId}-birthDate`} className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.birthDate")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input id={`${fieldId}-birthDate`} aria-invalid={!!errors.birthDate} aria-describedby={errors.birthDate ? `${fieldId}-birthDate-error` : undefined}
            type="date"
            value={buyerData.birthDate}
            onChange={(e) => { setBuyerData((p) => ({ ...p, birthDate: e.target.value })); setErrors((p) => ({ ...p, birthDate: "" })); }}
            className={errors.birthDate ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {errors.birthDate && <span id={`${fieldId}-birthDate-error`} className="text-[11px] text-destructive">{errors.birthDate}</span>}
        </div>

        {/* Telefone + CPF */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-phone`} className="text-xs font-semibold text-foreground">{t("checkout.buyerSection.phone")} <span className="text-primary ml-0.5">*</span></Label>
            <Input id={`${fieldId}-phone`} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? `${fieldId}-phone-error` : undefined}
              value={buyerData.phone}
              onChange={(e) => { setBuyerData((p) => ({ ...p, phone: formatPhone(e.target.value) })); setErrors((p) => ({ ...p, phone: "" })); }}
              placeholder={t("checkout.buyerSection.phonePlaceholder")}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={15}
              className={errors.phone ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.phone && <span id={`${fieldId}-phone-error`} className="text-[11px] text-destructive">{errors.phone}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <DocumentInput
              id={`${fieldId}-cpf`}
              aria-invalid={!!errors.cpf}
              aria-describedby={errors.cpf ? `${fieldId}-cpf-error` : undefined}
              value={buyerData.cpf}
              onChange={(val) => { setBuyerData((prev) => ({ ...prev, cpf: val })); setErrors((p) => ({ ...p, cpf: "" })); }}
              onValidation={() => {}}
              label={t("checkout.buyerSection.document").replace(/\s*\*$/, "")}
              placeholder={t("checkout.buyerSection.documentPlaceholder")}
              required
            />
            {!errors.cpf && <span className="text-[11px] text-muted-foreground">{t("checkout.buyerSection.documentNotice")}</span>}
            {errors.cpf && <span id={`${fieldId}-cpf-error`} className="text-[11px] text-destructive">{errors.cpf}</span>}
          </div>
        </div>

        {validateDocument(buyerData.cpf).valid && validateDocument(buyerData.cpf).type === "cnpj" && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-companyName`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.companyName")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input id={`${fieldId}-companyName`} aria-invalid={!!errors.companyName} aria-describedby={errors.companyName ? `${fieldId}-companyName-error` : undefined}
              placeholder={t("checkout.buyerSection.companyNamePlaceholder")}
              value={buyerData.companyName}
              onChange={(e) => {
                setBuyerData((prev) => ({ ...prev, companyName: e.target.value.slice(0, 100) }));
                setErrors((p) => ({ ...p, companyName: "" }));
              }}
              maxLength={100}
              className={errors.companyName ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.companyName && <span id={`${fieldId}-companyName-error`} className="text-[11px] text-destructive">{errors.companyName}</span>}
          </div>
        )}

        {/* Separator and Address Header */}
        <div className="border-t border-border/60 my-4 pt-4 col-span-full">
          <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">{t("checkout.buyerSection.billingAddress")}</h3>
        </div>

        {/* CEP */}
        <div className="flex flex-col gap-1 max-w-xs">
          <Label htmlFor={`${fieldId}-cep`} className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.cep")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <div className="relative">
            <Input id={`${fieldId}-cep`} aria-invalid={!!errors.cep} aria-describedby={errors.cep ? `${fieldId}-cep-error` : undefined}
              inputMode="numeric"
              autoComplete="postal-code"
              value={buyerData.cep}
              onChange={(e) => { handleCepChange(e.target.value); setErrors((p) => ({ ...p, cep: "" })); }}
              placeholder={t("checkout.buyerSection.cepPlaceholder")}
              maxLength={9}
              className={errors.cep ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {loadingCep && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          {errors.cep && <span id={`${fieldId}-cep-error`} className="text-[11px] text-destructive">{errors.cep}</span>}
        </div>

        {/* Rua + Número + Complemento */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px] gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-street`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.street")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input id={`${fieldId}-street`} aria-invalid={!!errors.street} aria-describedby={errors.street ? `${fieldId}-street-error` : undefined}
              autoComplete="address-line1"
              value={buyerData.street}
              onChange={(e) => { setBuyerData((p) => ({ ...p, street: e.target.value })); setErrors((p) => ({ ...p, street: "" })); }}
              placeholder={t("checkout.buyerSection.streetPlaceholder")}
              className={errors.street ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.street && <span id={`${fieldId}-street-error`} className="text-[11px] text-destructive">{errors.street}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-addressNumber`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.number")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input id={`${fieldId}-addressNumber`} aria-invalid={!!errors.addressNumber} aria-describedby={errors.addressNumber ? `${fieldId}-addressNumber-error` : undefined}
              value={buyerData.addressNumber}
              onChange={(e) => { setBuyerData((p) => ({ ...p, addressNumber: e.target.value })); setErrors((p) => ({ ...p, addressNumber: "" })); }}
              placeholder={t("checkout.buyerSection.numberPlaceholder")}
              className={errors.addressNumber ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.addressNumber && <span id={`${fieldId}-addressNumber-error`} className="text-[11px] text-destructive">{errors.addressNumber}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-complement`} className="text-xs font-semibold text-foreground">{t("checkout.buyerSection.complement")}</Label>
            <Input id={`${fieldId}-complement`} aria-invalid={!!errors.complement} aria-describedby={errors.complement ? `${fieldId}-complement-error` : undefined}
              value={buyerData.complement}
              onChange={(e) => setBuyerData((p) => ({ ...p, complement: e.target.value }))}
              placeholder={t("checkout.buyerSection.complementPlaceholder")}
            />
          </div>
        </div>

        {/* Bairro + Cidade + Estado */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-neighborhood`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.neighborhood")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input id={`${fieldId}-neighborhood`} aria-invalid={!!errors.neighborhood} aria-describedby={errors.neighborhood ? `${fieldId}-neighborhood-error` : undefined}
              value={buyerData.neighborhood}
              onChange={(e) => { setBuyerData((p) => ({ ...p, neighborhood: e.target.value })); setErrors((p) => ({ ...p, neighborhood: "" })); }}
              placeholder={t("checkout.buyerSection.neighborhoodPlaceholder")}
              className={errors.neighborhood ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.neighborhood && <span id={`${fieldId}-neighborhood-error`} className="text-[11px] text-destructive">{errors.neighborhood}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-city`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.city")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input id={`${fieldId}-city`} aria-invalid={!!errors.city} aria-describedby={errors.city ? `${fieldId}-city-error` : undefined}
              autoComplete="address-level2"
              value={buyerData.city}
              onChange={(e) => { setBuyerData((p) => ({ ...p, city: e.target.value })); setErrors((p) => ({ ...p, city: "" })); }}
              placeholder={t("checkout.buyerSection.cityPlaceholder")}
              className={errors.city ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.city && <span id={`${fieldId}-city-error`} className="text-[11px] text-destructive">{errors.city}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-state`} className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.state")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Select value={buyerData.state} onValueChange={(v) => { setBuyerData((p) => ({ ...p, state: v })); setErrors((p) => ({ ...p, state: "" })); }}>
              <SelectTrigger id={`${fieldId}-state`} aria-invalid={!!errors.state} aria-describedby={errors.state ? `${fieldId}-state-error` : undefined} className={errors.state ? "border-destructive focus:ring-destructive" : ""}>
                <SelectValue placeholder={t("checkout.buyerSection.selectState")} />
              </SelectTrigger>
              <SelectContent>
                {states.map((s) => (
                  <SelectItem key={s.sigla} value={s.sigla}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && <span id={`${fieldId}-state-error`} className="text-[11px] text-destructive">{errors.state}</span>}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
        <Link
          to="/carrinho"
          className="inline-flex min-h-11 items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("checkout.backToCart")}
        </Link>
        <button
          type="button"
          onClick={handleValidateAndNext}
          className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-primary text-white border-none text-[15px] font-semibold cursor-pointer shadow-sm shadow-black/15 transition-opacity hover:opacity-90"
        >
          {t("checkout.buyerSection.continue")}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
